import React, { useMemo, useRef } from "react";
import { makeStyles } from "@mui/styles";
import { Box, Container, Typography, Grid, Card, CardContent, IconButton, Avatar } from "@mui/material";
import Header from "components/Header/Header.js";
import Footer from "components/Footer/Footer.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { colors } from "components/Theme/WebTheme.js";
import { FONT_FAMILY } from "../common/sharedFunctions.js";
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const dashboardRoutes = [];

const useStyles = makeStyles((theme) => ({
  heroSection: {
    background: 'linear-gradient(135deg, #000000 0%, #0a0a0a 100%)',
    padding: '120px 0 80px',
    marginTop: '80px',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 100%)`,
      pointerEvents: 'none',
      zIndex: 1
    },
    '& > *': {
      position: 'relative',
      zIndex: 2
    }
  },
  valueCard: {
    background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
    borderRadius: '16px',
    padding: '32px',
    height: '100%',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    transition: 'all 0.3s ease',
    backgroundColor: 'transparent',
    '&:hover': {
      transform: 'translateY(-8px)',
      border: '1px solid rgba(212, 175, 55, 0.5)',
      boxShadow: '0 12px 40px rgba(212, 175, 55, 0.2)'
    }
  },
  iconBox: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    background: 'rgba(212, 175, 55, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px'
  },
  section: {
    padding: '80px 0',
    backgroundColor: '#000'
  },
  founderSection: {
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
    padding: '80px 0',
    position: 'relative'
  },
  statsBox: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    height: '100%'
  },
  fleetSection: {
    background: '#000',
    padding: '80px 0',
    position: 'relative',
    overflow: 'hidden'
  },
  driverCard: {
    background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
    borderRadius: '16px',
    padding: '24px',
    minWidth: '280px',
    maxWidth: '280px',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    transition: 'all 0.3s ease',
    backgroundColor: 'transparent',
    '&:hover': {
      transform: 'translateY(-8px)',
      border: '1px solid rgba(212, 175, 55, 0.5)',
      boxShadow: '0 12px 40px rgba(212, 175, 55, 0.2)'
    }
  }
}));

export default function AboutUs(props) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const classes = useStyles();
  const { ...rest } = props;
  const scrollRef = useRef(null);
  const usersdata = useSelector(state => state.usersdata);

  // Get all approved drivers with their vehicles
  const allDrivers = useMemo(() => {
    if (!usersdata?.users) return [];
    
    const drivers = usersdata.users
      .filter(user => 
        user.usertype === 'driver' && 
        user.approved && 
        user.car_image &&
        (user.vehicleMake || user.vehicleModel)
      )
      .map(user => ({
        firstName: user.firstName || 'Chauffeur',
        vehicleMake: user.vehicleMake || '',
        vehicleModel: user.vehicleModel || '',
        carImage: user.car_image,
        profilePic: user.profile_image
      }));

    // Shuffle drivers
    return drivers.sort(() => Math.random() - 0.5).slice(0, 12);
  }, [usersdata?.users]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const values = [
    {
      icon: <HandshakeIcon sx={{ fontSize: 32, color: colors.Button_Primary }} />,
      title: "10% de Commission",
      description: "Bien inférieur aux 20-28% des plateformes traditionnelles. Nous croyons en une rémunération équitable pour nos chauffeurs."
    },
    {
      icon: <EmojiPeopleIcon sx={{ fontSize: 32, color: colors.Button_Primary }} />,
      title: "Accompagnement Humain",
      description: "Formation continue, événements locaux, suivi personnalisé. Nous ne sommes pas qu'une app, nous sommes une communauté."
    },
    {
      icon: <DriveEtaIcon sx={{ fontSize: 32, color: colors.Button_Primary }} />,
      title: "Ancrage Local",
      description: "Implantation prioritaire en Occitanie avec Toulouse, Montpellier, Nîmes et Montauban, avant une expansion nationale."
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 32, color: colors.Button_Primary }} />,
      title: "Vision Durable",
      description: "Intégration progressive de véhicules électriques et bonnes pratiques écologiques pour un transport responsable."
    }
  ];

  return (
    <Box sx={{ margin: '-8px', backgroundColor: '#000', minHeight: '100vh' }}>
      <Header
        color="primary"
        routes={dashboardRoutes}
        rightLinks={<HeaderLinks />}
        fixed
        {...rest}
      />

      {/* Hero Section */}
      <Box className={classes.heroSection}>
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '32px', md: '48px' },
              fontWeight: 800,
              color: '#fff',
              textAlign: 'center',
              mb: 2,
              lineHeight: 1.2
            }}
          >
            La Nouvelle Génération du
            <Box component="span" sx={{ display: 'block', color: colors.Button_Primary }}>
              Transport Urbain
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '18px', md: '22px' },
              color: 'rgba(255,255,255,0.8)',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            10% de commission • 100% d'équité
          </Typography>
        </Container>
      </Box>

      {/* Story Section */}
      <Box className={classes.section}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '28px', md: '38px' },
              fontWeight: 700,
              color: '#fff',
              mb: 4,
              textAlign: 'center'
            }}
          >
            Notre Histoire
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '18px',
              color: 'rgba(255,255,255,0.8)',
              lineHeight: 1.8,
              mb: 3,
              textAlign: 'center',
              maxWidth: '900px',
              margin: '0 auto 32px'
            }}
          >
            Allô Driver est née d'une conviction simple : le transport VTC peut être plus juste, plus humain et plus rentable pour ceux qui le font vivre au quotidien.
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '16px',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: 1.8,
              mb: 2,
              maxWidth: '900px',
              margin: '0 auto 16px',
              textAlign: 'center'
            }}
          >
            Dans un marché dominé par des géants internationaux prélevant jusqu'à 28% de commission, nous avons imaginé une alternative : une plateforme où les chauffeurs conservent réellement le fruit de leur travail, où l'accompagnement humain prime sur l'algorithme froid, et où l'ancrage territorial crée une véritable communauté.
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '16px',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: 1.8,
              maxWidth: '900px',
              margin: '0 auto',
              textAlign: 'center'
            }}
          >
            Avec plus de 60 000 chauffeurs VTC en France et un marché pesant 4,5 milliards d'euros, l'Occitanie représente un territoire à fort potentiel encore sous-exploité. C'est là que notre histoire commence, avant de rayonner vers toute la France.
          </Typography>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ backgroundColor: '#0a0a0a', py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '28px', md: '38px' },
              fontWeight: 700,
              color: '#fff',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Nos Valeurs
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card className={classes.valueCard} sx={{ backgroundColor: 'transparent' }}>
                  <CardContent sx={{ backgroundColor: 'transparent' }}>
                    <Box className={classes.iconBox}>
                      {value.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#fff',
                        mb: 2
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.7)',
                        lineHeight: 1.6
                      }}
                    >
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Founder Section */}
      <Box className={classes.founderSection}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: { xs: '28px', md: '38px' },
                  fontWeight: 700,
                  color: '#fff',
                  mb: 3
                }}
              >
                Le Fondateur
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: 1.8,
                  mb: 2
                }}
              >
                Moussa Moustapha Tche-Tchere a créé sa société de VTC en 2014 à Toulouse et Montauban. Depuis plus de 10 ans, il a recruté et formé plus de 70 chauffeurs, développant une expertise unique du terrain et des réalités du métier.
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: 1.8
                }}
              >
                Pionnier dans l'utilisation des premières plateformes VTC à Toulouse, il a été témoin direct des transformations profondes qu'a connues le secteur. Aujourd'hui, fort de cette expérience, il porte une vision renouvelée : créer une plateforme qui respecte vraiment les chauffeurs tout en offrant une expérience client exceptionnelle.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box className={classes.statsBox}>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.6)',
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}
                    >
                      Depuis
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '42px',
                        fontWeight: 800,
                        color: colors.Button_Primary
                      }}
                    >
                      2014
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className={classes.statsBox}>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.6)',
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}
                    >
                      Siège social
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '18px',
                        fontWeight: 700,
                        color: colors.Button_Primary
                      }}
                    >
                      Montauban
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.7)'
                      }}
                    >
                      France
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className={classes.statsBox}>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.6)',
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}
                    >
                      Expérience
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '42px',
                        fontWeight: 800,
                        color: colors.Button_Primary
                      }}
                    >
                      10+
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.7)'
                      }}
                    >
                      ans
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className={classes.statsBox}>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.6)',
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}
                    >
                      Taille de la flotte
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '42px',
                        fontWeight: 800,
                        color: colors.Button_Primary
                      }}
                    >
                      50+
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.7)'
                      }}
                    >
                      véhicules
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Fleet Section */}
      <Box className={classes.fleetSection}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '28px', md: '38px' },
              fontWeight: 700,
              color: '#fff',
              mb: 2,
              textAlign: 'center'
            }}
          >
            Notre flotte hybride et électrique
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '18px',
              color: 'rgba(255,255,255,0.8)',
              lineHeight: 1.8,
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto 48px'
            }}
          >
            Découvrez notre sélection de véhicules hybrides et électriques, conduits par des chauffeurs professionnels. Notre objectif est de proposer des trajets clairs, confortables et suivis, selon les véhicules disponibles.
          </Typography>

          {/* Drivers Slider */}
          <Box sx={{ position: 'relative' }}>
            {/* Navigation Buttons */}
            <IconButton
              onClick={() => scroll('left')}
              sx={{
                position: 'absolute',
                left: { xs: -10, md: -20 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'rgba(212, 175, 55, 0.1)',
                color: colors.Button_Primary,
                width: 48,
                height: 48,
                border: '1px solid rgba(212, 175, 55, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(212, 175, 55, 0.2)',
                  border: '1px solid rgba(212, 175, 55, 0.5)',
                },
                display: { xs: 'none', md: 'flex' }
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            <IconButton
              onClick={() => scroll('right')}
              sx={{
                position: 'absolute',
                right: { xs: -10, md: -20 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'rgba(212, 175, 55, 0.1)',
                color: colors.Button_Primary,
                width: 48,
                height: 48,
                border: '1px solid rgba(212, 175, 55, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(212, 175, 55, 0.2)',
                  border: '1px solid rgba(212, 175, 55, 0.5)',
                },
                display: { xs: 'none', md: 'flex' }
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>

            {/* Scrollable Container */}
            <Box
              ref={scrollRef}
              sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                pb: 2,
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: colors.Button_Primary,
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#c4a137',
                  },
                },
              }}
            >
              {allDrivers.map((driver, index) => (
                <Card key={index} className={classes.driverCard} sx={{ backgroundColor: 'transparent' }}>
                  <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 }, backgroundColor: 'transparent' }}>
                    {/* Car Image */}
                    <Box
                      sx={{
                        width: '100%',
                        height: '180px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        mb: 2,
                        backgroundColor: 'rgba(212, 175, 55, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {driver.carImage ? (
                        <img
                          src={driver.carImage}
                          alt={`${driver.vehicleMake} ${driver.vehicleModel}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <DriveEtaIcon sx={{ fontSize: 64, color: 'rgba(212, 175, 55, 0.3)' }} />
                      )}
                    </Box>

                    {/* Driver Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Avatar
                        src={driver.profilePic}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid ${colors.Button_Primary}`,
                          bgcolor: 'rgba(212, 175, 55, 0.2)'
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 24 }} />
                      </Avatar>
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontSize: '16px',
                          fontWeight: 700,
                          color: '#fff'
                        }}
                      >
                        {driver.firstName}
                      </Typography>
                    </Box>

                    {/* Vehicle Info */}
                    <Box
                      sx={{
                        background: 'rgba(212, 175, 55, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid rgba(212, 175, 55, 0.2)'
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontSize: '14px',
                          color: colors.Button_Primary,
                          fontWeight: 600,
                          mb: 0.5
                        }}
                      >
                        Véhicule certifié
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontSize: '15px',
                          color: '#fff',
                          fontWeight: 600
                        }}
                      >
                        {driver.vehicleMake} {driver.vehicleModel}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* CTA */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '14px',
                color: 'rgba(255,255,255,0.6)',
                mb: 2
              }}
            >
              Tous nos chauffeurs sont formés aux standards Allô Driver
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                gap: 2,
                padding: '12px 24px',
                background: 'rgba(212, 175, 55, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(212, 175, 55, 0.2)'
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: '13px',
                  color: colors.Button_Primary,
                  fontWeight: 600
                }}
              >
                ✓ Formation interne
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: '13px',
                  color: colors.Button_Primary,
                  fontWeight: 600
                }}
              >
                ✓ Véhicules premium
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: '13px',
                  color: colors.Button_Primary,
                  fontWeight: 600
                }}
              >
                ✓ Service d'excellence
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box className={classes.section}>
        <Container maxWidth="lg">
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
              borderRadius: '24px',
              padding: { xs: '40px 24px', md: '64px 80px' },
              border: '1px solid rgba(212, 175, 55, 0.2)',
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: { xs: '28px', md: '38px' },
                fontWeight: 700,
                color: '#fff',
                mb: 3
              }}
            >
              Notre Mission
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '18px',
                color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.8,
                maxWidth: '800px',
                margin: '0 auto'
              }}
            >
              Rééquilibrer le secteur du VTC pour le rendre plus humain, plus équitable et plus performant. Accompagner chaque chauffeur dans l'amélioration de ses compétences, l'optimisation de sa rentabilité et le développement de son activité, tout en offrant aux clients un service de qualité ancré localement.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
